import React, { ReactNode } from 'react';
import { Alert, Typography, message } from 'antd';
import { Link } from 'react-router-dom';

import { Markdown } from '@components/Text';
import { getConfig } from '@components/Config';
import { useAppContext } from '@components/AppContext';
import { type AppContext } from '@components/AppContext/types';

const is2FaRequired = getConfig('is2FaRequired');
const consoleOrganizationId = getConfig<string>('consoleOrganizationId');

const MY_PROFILE_PATH = '/backstage/settings/my-profile';
const CONSOLE_MY_PROFILE_PATH = '/backstage/my-profile';

const LABEL_MESSAGE = `
  Two factor authentication should be enabled,
  [click here](MY_PROFILE_PATH) to proceed.
`;
const LABEL_REDIRECTING_TO_CONSOLE_ORG = 'Redirecting to console...';
const REDIRECTING_MESSAGE_KEY = 'redirecting-to-console-org';

type MarkdownLinkProps = {
  href?: string;
  children?: ReactNode;
}

const SecondFactorAlert = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const {
    identity,
    hasConsoleAccess,
    isConsoleOrganization,
    switchToOrganization,
  } = useAppContext() as AppContext;

  const { is2FaEnabled } = identity;

  if (!is2FaRequired) {
    return null;
  }

  if (is2FaEnabled) {
    return null;
  }

  const profilePath = isConsoleOrganization
    ? CONSOLE_MY_PROFILE_PATH
    : MY_PROFILE_PATH;
  const text = LABEL_MESSAGE
    .replace('MY_PROFILE_PATH', profilePath);

  const onLinkClick = async (
    event: React.MouseEvent<HTMLAnchorElement>,
  ) => {
    const shouldRedirectToConsoleOrganization =
      hasConsoleAccess &&
      !isConsoleOrganization;

    if (!shouldRedirectToConsoleOrganization) {
      return;
    }

    event.preventDefault();
    messageApi.open({
      key: REDIRECTING_MESSAGE_KEY,
      type: 'loading',
      content: LABEL_REDIRECTING_TO_CONSOLE_ORG,
      duration: 0,
    });

    try {
      await switchToOrganization(consoleOrganizationId, CONSOLE_MY_PROFILE_PATH);
    } finally {
      messageApi.destroy(REDIRECTING_MESSAGE_KEY);
    }
  };

  const components = {
    a(props: MarkdownLinkProps) {
      const { href, children } = props;

      return (
        <Typography.Text>
          <Link to={href || profilePath} onClick={onLinkClick}>
            {children}
          </Link>
        </Typography.Text>
      );
    },
  };

  return (
    <div className="header-alerts">
      {contextHolder}
      <Alert
        banner
        message={(
          <Markdown
            text={text}
            components={components}
          />
        )}
      />
    </div>
  );
};

export default SecondFactorAlert;
